// ProfileView.tsx — composed profile page shell.
// Renders all profile sections (banner → identity → status → bio → meta → stats →
// welcome → tabs → feed), publishes the right rail, and gates dialogs behind mount-on-open.

import { useState, useMemo } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ProfileChannel } from '@/features/networks/ContextTools';
import styled from 'styled-components';
import { Panel } from '@/ui';
import { media } from '@/theme/media';
import {
  useSession,
  useFollow,
  useSyncFollowState,
  usePageTitle,
  useRequireAuth,
} from '@/hooks';
import { ConnectionsDialog, type ConnectionKind } from './ConnectionsDialog';
import { useRightRail } from '@/app/shell/RightRail/context';
import { RailGroup, RailPanel } from '@/app/shell/RightRail';
import { GuestBanner } from '@/components/GuestBanner';
import { TrendingPanel } from '@/components/panels/TrendingPanel';
import type { User } from '@/core/models';
import { ProfileBanner } from './ProfileBanner';
import { ProfileIdentity } from './ProfileIdentity';
import { ProfileStatus } from './ProfileStatus';
import { ProfileBio } from './ProfileBio';
import { ProfileMeta } from './ProfileMeta';
import { ProfileStats } from './ProfileStats';
import { ProfileTabs } from './ProfileTabs';
import { ProfileFeed } from './ProfileFeed';
import { ProfileWelcome } from './ProfileWelcome';
import { ShareProfileDialog } from './ShareProfileDialog';
import { AboutPanel } from './panels/AboutPanel';
import { AchievementsPanel } from './panels/AchievementsPanel';
import { ProfileCommunitiesPanel } from './panels/ProfileCommunitiesPanel';
import { StatsOverviewPanel } from './panels/StatsOverviewPanel';
import { AboutThisUserPanel } from './panels/AboutThisUserPanel';
import { MutualConnectionsPanel } from './panels/MutualConnectionsPanel';
import { SuggestedProfilesPanel } from './panels/SuggestedProfilesPanel';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ProfileViewProps {
  user: User;
  isOwn: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isEmptyProfile(user: User): boolean {
  return (user.stats?.posts ?? 0) === 0 && (user.stats?.followers ?? 0) === 0;
}

// ─── Styled ───────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Reuse the card material so the member's glass, grain and radius still apply.
const HeaderCard = styled(Panel).attrs({ as: 'section' })`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 28px 24px 4px;
  ${media.mobile`padding: 24px 16px 4px;`}
`;

const ProfileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  margin-left: 96px;
  ${media.mobile`margin-left: 68px;`}
`;

// ─── Component ────────────────────────────────────────────────────────────────

export function ProfileView({ user, isOwn }: ProfileViewProps) {
  // All hooks unconditionally at the top — no conditional hooks on isOwn.
  const { isAuthenticated } = useSession();
  const { followerCountDelta } = useFollow(user.handle);
  // Re-sync from the server on mount — boot hydration only covers state as of
  // sign-in, so visiting a profile later (e.g. after the owner accepted a
  // request from elsewhere) would otherwise leave the button stuck on
  // "Requested" until a full reload. Own profile never needs this.
  useSyncFollowState(user.handle, !isOwn && isAuthenticated);
  const [activeTab, setActiveTab] = useState('posts');
  const navigate = useNavigate();
  // 1.27.70: Edit Profile is a page (/settings/profile), not a dialog. An old
  // /profile?edit=1 link (the account menu used it) still lands there.
  const [searchParams] = useSearchParams();
  const editFromLink = isOwn && searchParams.get('edit') === '1';
  const [shareOpen, setShareOpen] = useState(false);
  const [connections, setConnections] = useState<ConnectionKind | null>(null);
  const requireAuth = useRequireAuth();
  usePageTitle(`${user.name} (${user.handle}) / TruXSocial`);

  // Right rail — memoised so it only re-publishes when user identity or isOwn changes.
  const rail = useMemo(
    () =>
      isOwn ? (
        <RailGroup pageKey="profile-own">
          <RailPanel id="about" label="About">
            <AboutPanel user={user} />
          </RailPanel>
          <RailPanel id="achievements" label="Achievements">
            <AchievementsPanel user={user} />
          </RailPanel>
          <RailPanel id="communities" label="Communities">
            <ProfileCommunitiesPanel />
          </RailPanel>
          <RailPanel id="stats" label="Stats overview">
            <StatsOverviewPanel user={user} />
          </RailPanel>
        </RailGroup>
      ) : (
        <RailGroup pageKey="profile-other">
          <RailPanel id="about" label="About this user">
            <AboutThisUserPanel user={user} />
          </RailPanel>
          <RailPanel id="mutuals" label="Mutual connections">
            <MutualConnectionsPanel user={user} />
          </RailPanel>
          <RailPanel id="suggested" label="Suggested profiles">
            <SuggestedProfilesPanel />
          </RailPanel>
          <RailPanel id="trending" label="Trending">
            <TrendingPanel />
          </RailPanel>
        </RailGroup>
      ),
    [user, isOwn],
  );
  useRightRail(rail);

  return (
    <Wrap>
      {/* Guest banner — only for other-user profiles when not signed in */}
      {!isAuthenticated && !isOwn && (
        <GuestBanner message="You're viewing a profile on TruXSocial. Sign in to follow and reply." />
      )}

      <ProfileBanner user={user} />

      <HeaderCard>
        <ProfileIdentity
          user={user}
          isOwn={isOwn}
          onEditProfile={() => navigate('/settings/profile')}
          onShareProfile={() => setShareOpen(true)}
        />

        <ProfileDetails>
          <ProfileStatus accountId={user.id} />

          <ProfileBio user={user} />
          <ProfileChannel handle={user.handle} />

          <ProfileMeta user={user} />

          <ProfileStats
            user={user}
            isOwn={isOwn}
            followerDelta={followerCountDelta}
            onOpenConnections={(kind) => {
              // The lists are for signed-in members (the endpoint requires it).
              if (requireAuth('see this list')) setConnections(kind);
            }}
          />

          {/* Welcome card — own profile with no activity yet */}
          {isOwn && isEmptyProfile(user) && <ProfileWelcome />}
        </ProfileDetails>

        <ProfileTabs user={user} isOwn={isOwn} active={activeTab} onChange={setActiveTab} />
      </HeaderCard>

      <ProfileFeed user={user} isOwn={isOwn} active={activeTab} />

      {/* Dialogs mount conditionally so they reseed form state on every open */}
      {editFromLink && <Navigate to="/settings/profile" replace />}
      {shareOpen && (
        <ShareProfileDialog user={user} isOwn={isOwn} onClose={() => setShareOpen(false)} />
      )}
      {connections && (
        <ConnectionsDialog user={user} kind={connections} onClose={() => setConnections(null)} />
      )}
    </Wrap>
  );
}
